/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import Model, { Providers } from "@tripian/model";
import {
  Badge,
  BookingDetails,
  Button,
  CloseIconButton,
  CustomSlider,
  Modal,
  OfferCard,
  PreLoading,
  ReservationDetails,
  SvgIcons,
} from "@tripian/react";

import IFavoritePoi from "../../../../../../models/IFavoritePoi";
import useFavorite from "../../../../../../hooks/useFavorite";
import useFocus from "../../../../../../hooks/useFocus";
import useMyOffers from "../../../../../../hooks/useMyOffers";
import useReservation from "../../../../../../hooks/useReservation";
import LivnBookingTickets from "../../../../../TourInfo/LivnTourInfo/LivnBookingTickets";
import useStep from "../../../../../../hooks/useStep";
import useTranslate from "../../../../../../hooks/useTranslate";
import BookingCard from "../../../../../../components/BookingCard/BookingCard";
import PoiCard from "../../../../../../components/PoiCard/PoiCard";
import { CloneModal } from "../../../../../../components/CloneModal/CloneModal";
import ViatorBookingDetails from "../../../../../TourInfo/ViatorTourInfo/ViatorBookingDetails/ViatorBookingDetails";
import { QR_READER, QR_WRITER } from "../../../../../../constants/ROUTER_PATH_TITLE";
import classes from "./KiwiFavoritesContainer.module.scss";

type ActiveTab = "favorites" | "bookings" | "offers";

interface PlanIdStepDay {
  planId: number;
  step: Model.Step;
  dayIndex: number;
}

interface IKiwiFavoritesContainer {
  show: boolean;
  initialTab?: ActiveTab;
  dayIndex: number;
  tripHash: string;
  tripReadOnly: boolean;
  plans: Model.Plan[];
  poiCategoryGroups: Model.PoiCategoryGroup[];
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  toursLoading: boolean;
  topOffset?: string;
  timezone?: string;
  onBookingClick: () => void;
  close: () => void;
}

const KiwiFavoritesContainer: React.FC<IKiwiFavoritesContainer> = ({
  show,
  initialTab = "favorites",
  dayIndex,
  tripHash,
  tripReadOnly,
  plans,
  poiCategoryGroups,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  toursLoading,
  topOffset = "90px",
  timezone,
  onBookingClick,
  close,
}) => {
  const navigate = useNavigate();

  // ── tab state ──
  const [activeTab, setActiveTab] = useState<ActiveTab>("favorites");

  // ── favorites state ──
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
  const [activeMainCategory, setActiveMainCategory] = useState<string>("all");
  const [selectedPoiCategories, setSelectedPoiCategories] = useState<Model.PoiCategory[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [popupPosition, setPopupPosition] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  // ── bookings state ──
  const [yelpModalState, setYelpModalState] = useState<{ show: boolean; reservation?: Model.UserReservation }>({ show: false });
  const [gygModalState, setGygModalState] = useState<{ show: boolean; reservation?: Model.UserReservation }>({ show: false });
  const [viatorModalState, setViatorModalState] = useState<{ show: boolean; reservation?: Model.UserReservation }>({ show: false });
  const [kiwiModalState, setKiwiModalState] = useState<{ show: boolean; reservation?: Model.UserReservation }>({ show: false });
  const [livnModalState, setLivnModalState] = useState<{ show: boolean; reservation?: Model.UserReservation }>({ show: false });

  // ── hooks ──
  const { cityId, favorites, favoriteAdd, favoriteDelete, loadingFavorites, loadingFavoritePoi, favoritesFetch } = useFavorite();
  const { stepAdd, stepDelete } = useStep();
  const { focusPoi } = useFocus();
  const { t } = useTranslate();
  const {
    yelpReservations, gygReservations, viatorReservations, kiwiReservations, livnReservations,
    reservationCancel, loadingReservations, loadingReservation, initReservations,
  } = useReservation();
  const { loadingMyAllOffers, myAllOffers, isLoadingOffer, offerOptIn, offerOptOut } = useMyOffers();

  // ── favorites data fetching ──
  useEffect(() => {
    if (favorites === undefined && loadingFavorites === false && cityId !== undefined) favoritesFetch();
  }, [cityId, favorites, favoritesFetch, loadingFavorites]);

  useEffect(() => {
    initReservations();
  }, [initReservations]);

  // Reset on open, apply initialTab
  useEffect(() => {
    if (show) {
      setActiveTab(initialTab);
      setActiveMainCategory("all");
      setSearchText("");
      setSelectedTags([]);
      setShowFilters(false);
    }
  }, [show, initialTab]);

  useEffect(() => {
    setSelectedPoiCategories(poiCategoryGroups.flatMap(g => g.categories));
  }, [poiCategoryGroups]);

  // ── plan/step helpers ──
  const planStepDays: PlanIdStepDay[] = useMemo(() => {
    return plans.reduce((acc: PlanIdStepDay[], plan: Model.Plan, idx: number) => {
      if (!plan) return acc;
      return acc.concat(plan.steps.map(step => ({ planId: plan.id, step, dayIndex: idx })));
    }, []);
  }, [plans]);

  const currentPlanId = useMemo(
    () => (dayIndex < plans.length ? plans[dayIndex].id : 0),
    [dayIndex, plans]
  );

  const poiPlanStepDay = useCallback(
    (poiId: string): PlanIdStepDay | undefined =>
      planStepDays.find(s => s.step.poi.id === poiId && s.dayIndex === dayIndex),
    [dayIndex, planStepDays]
  );

  const isCurrentDayStep = useCallback((poiId: string): boolean => !!poiPlanStepDay(poiId), [poiPlanStepDay]);

  const findDaysNumbers = (poiId: string): number[] | undefined => {
    if (!poiId || !plans) return undefined;
    const result: number[] = [];
    for (let i = 0; i < plans.length; i++) {
      if (plans[i].steps.findIndex(s => s.poi.id === poiId) > -1) result.push(i + 1);
    }
    return result;
  };

  // ── category filter helpers ──
  const activeCategoryGroups = useMemo(() => {
    if (!favorites || favorites.length === 0) return poiCategoryGroups;
    const favCatIds = new Set(favorites.flatMap(f => f.poi.category.map(c => c.id)));
    return poiCategoryGroups.filter(group => group.categories.some(c => favCatIds.has(c.id)));
  }, [favorites, poiCategoryGroups]);

  const mainCategoryGroups = useMemo(() => [
    { id: "all", name: t("trips.categories.all") },
    ...activeCategoryGroups.map(g => ({ id: g.name, name: g.name })),
  ], [activeCategoryGroups, t]);

  const handleMainCategorySelect = (categoryId: string) => {
    setActiveMainCategory(categoryId);
    if (categoryId === "all") {
      setSelectedPoiCategories(poiCategoryGroups.flatMap(g => g.categories));
    } else {
      const group = poiCategoryGroups.find(g => g.name === categoryId);
      setSelectedPoiCategories(group ? group.categories : []);
    }
  };

  const selectedPoiCategoryIds = useMemo(() => selectedPoiCategories.map(c => c.id), [selectedPoiCategories]);

  const availableTags = useMemo(() => {
    if (!favorites || favorites.length === 0) return [];
    const tagSet = new Set<string>();
    favorites.forEach(f => f.poi.tags.forEach(tag => { if (tag) tagSet.add(tag); }));
    return Array.from(tagSet);
  }, [favorites]);

  const filteredFavorites = useMemo(() => {
    if (!favorites) return [];
    return favorites.filter(fp => {
      const poiCatIds = fp.poi.category.map(c => c.id);
      if (selectedPoiCategoryIds.length > 0 && !poiCatIds.some(id => selectedPoiCategoryIds.includes(id))) return false;
      if (searchText.trim()) {
        const q = searchText.toLowerCase();
        if (
          !fp.poi.name.toLowerCase().includes(q) &&
          !fp.poi.tags.some(tag => tag.toLowerCase().includes(q)) &&
          !fp.poi.category.some(c => c.name.toLowerCase().includes(q))
        ) return false;
      }
      if (selectedTags.length > 0 && !selectedTags.some(tag => fp.poi.tags.includes(tag))) return false;
      return true;
    });
  }, [favorites, selectedPoiCategoryIds, searchText, selectedTags]);

  useEffect(() => {
    if (showFilters && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      setPopupPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
  }, [showFilters]);

  // ── favorites actions ──
  const cardButtonOnClick = useCallback(
    (poi: Model.Poi, from?: string, to?: string) => {
      const planStepDay = poiPlanStepDay(poi.id);
      if (tripReadOnly) setShowCloneModal(true);
      else if (planStepDay) stepDelete(planStepDay.step.id);
      else stepAdd(currentPlanId, poi.id, from, to);
    },
    [currentPlanId, poiPlanStepDay, stepAdd, stepDelete, tripReadOnly]
  );

  const favoriteToggle = (poi: Model.Poi, add: boolean) => {
    if (add) {
      favoriteAdd(poi.id);
    } else {
      const favoriteId = favorites?.find(f => f.poiId === poi.id)?.id || 0;
      if (favoriteId) favoriteDelete(favoriteId, poi.id);
    }
  };

  const isFavorite = (poiId: string) =>
    favorites ? favorites.findIndex(f => f.poiId === poiId) > -1 : false;

  // ── offers helper ──
  const currentPlanDate = useMemo(
    () => (plans && dayIndex < plans.length ? plans[dayIndex].date : undefined),
    [dayIndex, plans]
  );

  // ── render helpers ──
  const renderFavorites = () => {
    if (loadingFavorites && favorites === undefined) return <PreLoading />;
    if (!favorites) return null;
    if (favorites.length === 0)
      return <div className="center mt-8"><span>{t("trips.myTrips.itinerary.favorites.emptyMessage")}</span></div>;
    if (filteredFavorites.length === 0)
      return <div className="center mt-8"><span>{t("trips.myTrips.itinerary.favorites.emptyMessage")}</span></div>;

    return filteredFavorites.map((favoritePoi: IFavoritePoi) =>
      loadingFavoritePoi(favoritePoi.poiId) ? (
        <div key={`fav-loading-${favoritePoi.id}`} className={classes.favoriteItemsLoading}>
          <PreLoading size="small" />
        </div>
      ) : (
        <PoiCard
          key={`fav-card-${favoritePoi.id}`}
          poi={favoritePoi.poi}
          inStep={isCurrentDayStep(favoritePoi.poi.id)}
          isFavorite={isFavorite(favoritePoi.poi.id)}
          isLoadingFavorite={loadingFavoritePoi(favoritePoi.poi.id)}
          dayNumbers={findDaysNumbers(favoritePoi.poi.id)}
          TOUR_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
          TICKET_PROVIDER_IDS={window.tconfig.TOUR_TICKET_PROVIDER_IDS}
          RESTAURANT_RESERVATION_PROVIDER_IDS={window.tconfig.RESTAURANT_RESERVATION_PROVIDER_IDS}
          hideTourTicketIcons={!window.tconfig.SHOW_TOURS_AND_TICKETS}
          gygTourIds={gygTourIds}
          bbTourIds={bbTourIds}
          viatorTourIds={viatorTourIds}
          toristyTourIds={toristyTourIds}
          tourTicketProductsLoading={toursLoading}
          poiCardClicked={focusPoi}
          onAddPoiClick={cardButtonOnClick}
          onFavoriteToggle={favoriteToggle}
          onBookingClick={() => { focusPoi(favoritePoi.poi); onBookingClick(); }}
          t={t}
        />
      )
    );
  };

  const renderBookings = () => {
    if (loadingReservations) return <PreLoading />;

    const hasBookings =
      (yelpReservations && yelpReservations.length > 0) ||
      (gygReservations && gygReservations.length > 0) ||
      (viatorReservations && viatorReservations.length > 0) ||
      (kiwiReservations && kiwiReservations.length > 0) ||
      (livnReservations && livnReservations.length > 0);

    if (!hasBookings)
      return <div className="center mt-8"><span>{t("trips.myTrips.itinerary.bookings.emptyMessage")}</span></div>;

    return (
      <div className="flex flex-col justify-center items-center w-fit my-0 mx-auto p-4 rounded-2xl">
        {(yelpReservations || []).map(res => {
          const info = res.value as Providers.Yelp.ReservationInfo;
          return (
            <BookingCard
              key={info.reservation_id}
              image={info.restaurant_image}
              title={info.restaurant_name}
              dateTime={`${info.reservation_details.date}${info.reservation_details.time ? `, ${info.reservation_details.time}` : ""}`}
              person={info.reservation_details.covers === 1 ? "1 person" : `${info.reservation_details.covers} people`}
              provider="YELP"
              pastBooking={moment().isAfter(moment(res.bookingDateTime))}
              clicked={() => setYelpModalState({ show: true, reservation: res })}
              t={t}
            />
          );
        })}
        {(gygReservations || []).map(res => {
          const info = res.value as Providers.Gyg.TourReservationDetails;
          return (
            <BookingCard
              key={info.data.shopping_cart.shopping_cart_hash}
              image={info.data.shopping_cart.tour_image}
              title={info.data.shopping_cart.tour_name}
              dateTime={moment(info.data.shopping_cart.bookings[0].bookable.datetime).format("MMM DD - HH:mm")}
              person={info.data.shopping_cart.bookings.length === 1 ? "1 person" : `${info.data.shopping_cart.bookings.length} people`}
              provider="GETYOURGUIDE"
              pastBooking={moment().isAfter(moment(res.bookingDateTime))}
              clicked={() => setGygModalState({ show: true, reservation: res })}
              t={t}
            />
          );
        })}
        {(viatorReservations || [])
          .sort((a, b) => moment(b.bookingDateTime).diff(moment(a.bookingDateTime)))
          .map(res => {
            const info = res.value as Providers.Viator.BookingReservationDetails;
            const travelers = info?.items[0].lineItems?.[0].numberOfTravelers;
            return (
              <BookingCard
                key={info.cartRef}
                image={info.tourImage}
                title={info.tourName}
                dateTime={`${info.availableDate}${info.selectedHour ? `, ${info.selectedHour}` : ""}`}
                person={travelers ? (travelers === 1 ? `1 ${t("trips.myTrips.localExperiences.tourDetails.person")}` : `${travelers} ${t("trips.myTrips.localExperiences.tourDetails.people")}`) : undefined}
                status={info.items[0].status}
                provider="VIATOR"
                pastBooking={moment().isAfter(moment(res.bookingDateTime))}
                clicked={() => setViatorModalState({ show: true, reservation: res })}
                t={t}
              />
            );
          })}
        {(kiwiReservations || []).map(res => {
          const info = res.value as Providers.Kiwi.KiwiReservationData & {
            hotelTitle?: string;
            hotelImage?: string;
            hotelCity?: string;
          };
          return (
            <BookingCard
              key={info.confirmationId}
              image={info.hotelImage || ""}
              title={info.hotelTitle || ""}
              dateTime={`${info.inDate} — ${info.outDate}`}
              person={info.adults ? `${info.adults} ${Number(info.adults) === 1 ? "person" : "people"}` : undefined}
              provider="KIWI"
              pastBooking={moment().isAfter(moment(res.bookingDateTime))}
              clicked={() => setKiwiModalState({ show: true, reservation: res })}
              t={t}
            />
          );
        })}
        {(livnReservations || []).map(res => {
          const info = res.value as {
            livnReference: string;
            bookings: Providers.Livn.Booking[];
            tourName?: string;
            tourImage?: string;
          };
          return (
            <BookingCard
              key={info.livnReference}
              image={info.tourImage || ""}
              title={info.tourName || ""}
              dateTime={moment(res.bookingDateTime).format("MMM DD, YYYY")}
              provider="LIVN"
              pastBooking={moment().isAfter(moment(res.bookingDateTime).endOf("day"))}
              clicked={() => setLivnModalState({ show: true, reservation: res })}
              t={t}
            />
          );
        })}
      </div>
    );
  };

  const renderOffers = () => {
    if (loadingMyAllOffers && myAllOffers === undefined) return <PreLoading />;
    if (!myAllOffers) return null;
    if (myAllOffers.length === 0)
      return <div className="center mt-8"><span>{t("trips.myTrips.itinerary.offers.myOffers.emptyOffersMessage")}</span></div>;

    return myAllOffers.map((offerPoi: Model.Poi) =>
      offerPoi.offers.map((offer: Model.Offer, index) => (
        <div className="my-2" key={index}>
          <OfferCard
            key={`offer-${offer.id}`}
            offer={offer}
            planDate={currentPlanDate}
            isMyOffer
            optClicked={(optIn: boolean, id: number) => {
              if (currentPlanDate) {
                if (optIn) {
                  const dateTime = moment(currentPlanDate)
                    .set({ hour: moment().hour(), minute: moment().minute(), second: moment().second() })
                    .tz(timezone || "UTC")
                    .format("YYYY-MM-DD HH:mm:ss");
                  offerOptIn(id, dateTime);
                } else {
                  offerOptOut(id);
                }
              }
            }}
            redeemClicked={() => {
              if (window.tconfig.QR_READER === "business") navigate(QR_WRITER.PATH + `/${offer.id}`);
              else navigate(QR_READER.PATH);
            }}
            cardClicked={() => focusPoi(offerPoi)}
            isLoadingOffer={isLoadingOffer}
            optedIn={myAllOffers?.some(p => p.offers.some(x => x.id === offer.id)) || false}
            poiName={offerPoi.name}
            redeemText={window.tconfig.QR_READER === "business" ? "View QR Code" : "Redeem"}
          />
        </div>
      ))
    );
  };

  if (!show) return null;

  return ReactDOM.createPortal(
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={classes.overlay}
        onClick={close}
        style={{ top: topOffset, height: `calc(100% - ${topOffset})` }}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className={classes.panel} onClick={e => e.stopPropagation()}>

          {/* Close button */}
          <div className="px-6 pt-5 pb-2">
            <button type="button" className="w-7 h-7 flex items-center justify-center text-gray-700 hover:text-gray-900" onClick={close} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-end px-6 border-b border-gray-200">
            <button type="button" className={activeTab === "favorites" ? classes.tab : classes.tabInactive} onClick={() => setActiveTab("favorites")}>
              {t("trips.myTrips.itinerary.favorites.title")}
            </button>
            <button type="button" className={activeTab === "bookings" ? classes.tab : classes.tabInactive} onClick={() => setActiveTab("bookings")}>
              {t("trips.myTrips.itinerary.bookings.title")}
            </button>
            <button type="button" className={activeTab === "offers" ? classes.tab : classes.tabInactive} onClick={() => setActiveTab("offers")}>
              {t("trips.myTrips.itinerary.offers.myOffers.title")}
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">

            {/* ── FAVORITES TAB ── */}
            {activeTab === "favorites" && (
              <>
                {/* Category pills — wrapping */}
                <div className="flex flex-wrap gap-2 px-6 py-4 border-b border-gray-200">
                  {mainCategoryGroups.map((category, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`text-sm rounded-full border px-4 py-[0.4375rem] font-medium whitespace-nowrap transition-colors ${
                        activeMainCategory === category.id
                          ? "text-white bg-primary-color border-primary-color"
                          : "text-gray-800 bg-white border-gray-300 hover:border-gray-400"
                      }`}
                      onClick={() => handleMainCategorySelect(category.id)}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>

                {/* Search Bar + Filter Button */}
                <div className="px-6 pt-4 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <SvgIcons.Search className="absolute start-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        className="w-full ps-10 pe-10 py-3 rounded-full border border-gray-200 bg-gray-100 text-sm focus:outline-none focus:bg-white focus:border-gray-300 transition-colors"
                        placeholder={t("trips.myTrips.exploreMore.placeholder")}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                      />
                      {searchText && (
                        <button type="button" className="absolute end-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSearchText("")}>
                          <SvgIcons.SearchClose />
                        </button>
                      )}
                    </div>
                    <div className="relative flex-shrink-0">
                      <button
                        ref={filterButtonRef}
                        type="button"
                        className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors border border-gray-200"
                        onClick={() => setShowFilters(prev => !prev)}
                        aria-haspopup="true"
                        aria-expanded={showFilters}
                      >
                        <SvgIcons.Filter2 />
                      </button>
                      {showFilters && (
                        <>
                          <div className="fixed inset-0 z-40" style={{ pointerEvents: "all" }} onClick={() => setShowFilters(false)} />
                          <div
                            className="fixed w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                            style={{ top: `${popupPosition.top}px`, right: `${popupPosition.right}px`, maxHeight: "70vh" }}
                            onClick={e => e.stopPropagation()}
                          >
                            <div className="p-4 border-b flex justify-between items-center bg-white rounded-t-lg">
                              <h3 className="font-semibold text-gray-800">{t("trips.filters.title")}</h3>
                              <div className="flex items-center">
                                <button type="button" className="text-sm bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 text-gray-700 me-3" onClick={() => { setSelectedPoiCategories(poiCategoryGroups.flatMap(g => g.categories)); setShowFilters(false); }}>
                                  {t("trips.filters.clearAll")}
                                </button>
                                <button type="button" onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                                  <SvgIcons.SearchClose width="18" height="18" />
                                </button>
                              </div>
                            </div>
                            <div className="max-h-[400px] overflow-y-auto">
                              <div className="p-4">
                                {(activeMainCategory === "all" ? activeCategoryGroups : activeCategoryGroups.filter(g => g.name === activeMainCategory)).map(group => (
                                  <div key={`fg-${group.name}`} className="mb-5">
                                    <h4 className="font-medium mb-3 text-gray-700 border-s-4 border-primary-color ps-2">{group.name}</h4>
                                    <div className="grid grid-cols-1 gap-2 ps-2">
                                      {group.categories.map(cat => (
                                        <div key={`fc-${cat.id}`} className="flex items-center hover:bg-gray-50 rounded-md p-1.5 -ms-1.5">
                                          <div className="relative w-5 h-5">
                                            <input
                                              type="checkbox"
                                              id={`fav-cat-${cat.id}`}
                                              checked={selectedPoiCategoryIds.includes(cat.id)}
                                              onChange={() => {
                                                setSelectedPoiCategories(prev =>
                                                  selectedPoiCategoryIds.includes(cat.id)
                                                    ? prev.filter(c => c.id !== cat.id)
                                                    : [...prev, cat]
                                                );
                                              }}
                                              className="appearance-none h-5 w-5 rounded-md bg-white border border-gray-300 checked:border-primary-color checked:bg-primary-color checked:border-0 focus:outline-none focus:ring-0 transition-colors"
                                            />
                                            <SvgIcons.Check2
                                              className={`absolute start-0.5 top-0.5 w-4 h-4 text-white pointer-events-none ${selectedPoiCategoryIds.includes(cat.id) ? "opacity-100" : "opacity-0"}`}
                                              fill="white"
                                            />
                                          </div>
                                          <label htmlFor={`fav-cat-${cat.id}`} className="ms-2.5 text-gray-700 cursor-pointer text-sm">{cat.name}</label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick filter tags */}
                {availableTags.length > 0 && (
                  <div className="px-6 pb-3 border-b border-gray-200">
                    <CustomSlider scrollSize={150}>
                      {availableTags.map(tag => (
                        <button
                          key={`qf-${tag}`}
                          type="button"
                          className={`px-4 py-[0.375rem] text-sm rounded-full whitespace-nowrap transition-all ${
                            selectedTags.includes(tag)
                              ? "border-2 border-primary-color text-primary-color font-medium bg-white shadow-sm"
                              : "border border-gray-300 text-gray-700 bg-white hover:border-gray-400"
                          }`}
                          onClick={() => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                        >
                          {tag}
                        </button>
                      ))}
                    </CustomSlider>
                  </div>
                )}

                {/* Active tag badges */}
                {selectedTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-6 py-3">
                    {selectedTags.map((tag, i) => (
                      <Badge key={`b-${i}-${tag}`} name={tag} onClose={() => setSelectedTags(prev => prev.filter(t => t !== tag))} />
                    ))}
                    <button type="button" className="text-xs bg-gray-200 rounded-full px-3 py-1 text-gray-800 hover:bg-gray-300 transition-colors" onClick={() => setSelectedTags([])}>
                      {t("trips.filters.clearAll")}
                    </button>
                  </div>
                )}

                <div className="px-4 mt-2 pb-8">{renderFavorites()}</div>
              </>
            )}

            {/* ── BOOKINGS TAB ── */}
            {activeTab === "bookings" && (
              <div className="pb-8">{renderBookings()}</div>
            )}

            {/* ── OFFERS TAB ── */}
            {activeTab === "offers" && (
              <div className="px-4 pb-8">{renderOffers()}</div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCloneModal && <CloneModal sharedTripHash={tripHash ?? ""} onCancel={() => setShowCloneModal(false)} />}

      {/* Yelp reservation modal */}
      <Modal show={yelpModalState.show} className="p5" backdropClick={() => { if (!loadingReservation) setYelpModalState({ show: false }); }}>
        <div className="row mb0">
          <div className="m2">
            <CloseIconButton fill="#fff" clicked={() => { if (!loadingReservation) setYelpModalState({ show: false }); }} />
          </div>
          {yelpModalState.reservation && (
            <div>
              {loadingReservation && <PreLoading bgColor="rgba(238,238,238,0.8)" />}
              <ReservationDetails reservationInfo={yelpModalState.reservation.value as Providers.Yelp.ReservationInfo} t={t} />
              {moment(yelpModalState.reservation.bookingDateTime).isAfter(moment()) && (
                <div className="row center">
                  <Button color="primary" text="Cancel Reservation" onClick={() => {
                    const info = yelpModalState.reservation!.value as Providers.Yelp.ReservationInfo;
                    reservationCancel(info.reservation_id || "")?.then(() => setYelpModalState({ show: false }));
                  }} />
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* GYG reservation modal */}
      <Modal show={gygModalState.show} className="booking-modal p5" backdropClick={() => { if (!loadingReservation) setGygModalState({ show: false }); }}>
        <div className="row m0">
          <div className="m2">
            <CloseIconButton fill="#fff" clicked={() => { if (!loadingReservation) setGygModalState({ show: false }); }} />
          </div>
          {gygModalState.reservation && (
            <div>
              {loadingReservation && <PreLoading bgColor="rgba(238,238,238,0.8)" />}
              <BookingDetails bookingInfo={gygModalState.reservation.value as Providers.Gyg.TourReservationDetails} />
              <div className="text-sm text-gray-600 text-center mt-2">Manage ticket through email confirmation from provider or click link above.</div>
            </div>
          )}
        </div>
      </Modal>

      {/* Viator reservation modal */}
      <Modal show={viatorModalState.show} className="!min-w-[90%] !w-[90%] bg-background-color md:!min-w-[50%] md:!w-32 shadow-2xl rounded-3xl" backdropClick={() => { if (!loadingReservation) setViatorModalState({ show: false }); }}>
        <div className="row m0 p-4">
          <div className="absolute m-2 top-0 end-0 w-9 h-9 cursor-pointer drop-shadow-[0_3px_2px_rgba(0,0,0,1)]">
            <CloseIconButton fill="#fff" clicked={() => { if (!loadingReservation) setViatorModalState({ show: false }); }} />
          </div>
          {viatorModalState.reservation && (() => {
            const info = viatorModalState.reservation.value as Providers.Viator.BookingReservationDetails;
            return (
              <div className="w-full">
                <ViatorBookingDetails
                  items={info.items}
                  totalConfirmedPrice={info.totalConfirmedPrice}
                  voucherInfo={info.voucherInfo}
                  tourName={info.tourName}
                  tourImage={info.tourImage}
                  travelHour={info.selectedHour}
                  travelDate={info.availableDate}
                  loading={loadingReservation}
                  showButton={moment(viatorModalState.reservation.bookingDateTime).isAfter(moment())}
                  buttonText={t("trips.myTrips.localExperiences.tourDetails.cancelReservation")}
                  clicked={() => {
                    reservationCancel(info.items[0].bookingRef || "")?.then(() => setViatorModalState({ show: false }));
                  }}
                  t={t}
                />
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* Kiwi reservation modal */}
      <Modal show={kiwiModalState.show} className="booking-modal p5" backdropClick={() => setKiwiModalState({ show: false })}>
        <div className="row m0">
          <div className="m2">
            <CloseIconButton fill="#fff" clicked={() => setKiwiModalState({ show: false })} />
          </div>
          {kiwiModalState.reservation && (() => {
            const info = kiwiModalState.reservation.value as Providers.Kiwi.KiwiReservationData & {
              hotelTitle?: string;
              hotelImage?: string;
              hotelCity?: string;
            };
            return (
              <div className="p-4">
                <h3 className="font-semibold mb-2">{info.hotelTitle}</h3>
                {info.hotelCity && <p>{info.hotelCity}</p>}
                <p>{info.roomTitle}</p>
                <p>{info.rateTitle}</p>
                <p>{info.inDate} — {info.outDate}</p>
                <p>{info.totalCostInclusive} {info.currencyCode}</p>
                <p className="text-sm text-gray-500 mt-2">Confirmation: {info.confirmationId}</p>
                {info.policies?.cancellation && (
                  <p className="text-sm text-gray-500 mt-2">{info.policies.cancellation}</p>
                )}
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* Livn reservation modal */}
      <Modal show={livnModalState.show} className="booking-modal p5" backdropClick={() => setLivnModalState({ show: false })}>
        <div className="row m0">
          <div className="m2">
            <CloseIconButton fill="#fff" clicked={() => setLivnModalState({ show: false })} />
          </div>
          {livnModalState.reservation && (() => {
            const info = livnModalState.reservation.value as {
              livnReference: string;
              bookings: Providers.Livn.Booking[];
              tourName?: string;
              tourImage?: string;
              markupPerc?: number;
            };
            return (
              <div className="p-4">
                <h3 className="font-semibold mb-2">{info.tourName}</h3>
                <p className="text-sm text-gray-500 mt-2 mb-3">Reference: {info.livnReference}</p>
                <div className="flex flex-col gap-3">
                  <LivnBookingTickets
                    bookings={info.bookings ?? []}
                    markupPerc={info.markupPerc}
                    tourName={info.tourName}
                    fallbackTravelDate={livnModalState.reservation?.bookingDateTime}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </Modal>
    </>,
    document.body
  );
};

export default KiwiFavoritesContainer;